// HR & People API calls. Paths match backend/app/api/v1/endpoints/{employee,leave,attendance,talent,compliance}_endpoints.py.
import { http } from '../api';

const { get, fresh, post, put, patch, del, upload, blob } = http;

export const qs = params => {
  const q = new URLSearchParams();
  Object.entries(params || {}).forEach(([k, v]) => { if (v !== undefined && v !== null && v !== '') q.set(k, v); });
  const s = q.toString();
  return s ? `?${s}` : '';
};

// paged endpoints answer { total, items }; plain lists answer an array — screens always get an array
export const items = r => (Array.isArray(r) ? r : r?.items || []);
const all = path => get(path).then(items);
const PAGE = 500;

// ── employees ────────────────────────────────────────────────────────────────
export const listEmployees = (params = {}) => all(`/employees${qs({ limit: PAGE, ...params })}`);
export const getEmployee = id => get(`/employees/${id}`);
// one page of the registry: { items, total } (the server reports the full match count in X-Total-Count)
export const listEmployeesPage = (params = {}) => get(`/employees${qs(params)}`).then(r => {
  const rows = items(r);
  return { items: rows, total: Number.isFinite(r?.totalCount) ? r.totalCount : rows.length };
});
export const createEmployee = body => post('/employees', body);
export const updateEmployee = (id, body) => put(`/employees/${id}`, body);
export const setEmployeeStatus = (id, body) => patch(`/employees/${id}/status`, body);
export const updatePersonalInfo = (id, body) => put(`/employees/${id}/personal-info`, body);
export const updateStatutory = (id, body) => put(`/employees/${id}/statutory-and-bank`, body);
export const updateExpatriate = (id, body) => put(`/employees/${id}/expatriate-detail`, body);
export const updateProfileDetail = (id, body) => put(`/employees/${id}/profile-detail`, body);
export const updateCompensation = (id, body) => put(`/employees/${id}/compensation`, body);
export const updateWorkRules = (id, body) => put(`/employees/${id}/work-rules`, body);
export const addReference = (id, body) => post(`/employees/${id}/references`, body);
export const updateReference = (rid, body) => put(`/employees/references/${rid}`, body);
export const setReferenceStatus = (rid, status) => put(`/employees/references/${rid}/status`, { status });
export const deleteReference = rid => del(`/employees/references/${rid}`);
export const addEmergencyContact = (id, body) => post(`/employees/${id}/emergency-contacts`, body);
export const updateEmergencyContact = (cid, body) => put(`/employees/emergency-contacts/${cid}`, body);
export const deleteEmergencyContact = cid => del(`/employees/emergency-contacts/${cid}`);
export const addDependent = (id, body) => post(`/employees/${id}/dependents`, body);
export const updateDependent = (did, body) => put(`/employees/dependents/${did}`, body);
export const deleteDependent = did => del(`/employees/dependents/${did}`);
export const listDependents = (params = {}) => all(`/employees/dependents${qs(params)}`);
export const getEmployeeStats = () => get('/employees/stats/summary');
// Complete Employee Records computed on the server, one page at a time: { items, total, summary, checks }
export const employeeCompletenessPage = (params = {}) => get(`/employees/completeness${qs(params)}`);
export const setEmployeeDepartments = (id, departmentIds, primaryId) => put(`/employees/${id}/departments`, { department_ids: departmentIds, primary_department_id: primaryId || null });
// every user account gets an employee record (super admin)
export const syncEmployeesFromUsers = () => post('/employees/sync-from-users', {});

export const listDesignations = (params = {}) => all(`/designations${qs(params)}`);
export const createDesignation = body => post('/designations', body);

// ── org lookups ──────────────────────────────────────────────────────────────
export const listDepartments = () => get('/departments/?limit=500').then(r => r?.departments || items(r));
export const listBranches = () => all('/org/branches');
// /org/sites is administrator-only; the attendance register lists the same sites to every signed-in user
export const listSites = () => all('/org/sites').catch(() => get('/attendance/day').then(r => r?.sites || []));

// ── leave & welfare ──────────────────────────────────────────────────────────
export const listLeaveTypes = (params = {}) => all(`/leave-types${qs(params)}`);
export const listHolidays = year => all(`/holidays${qs({ year })}`);
// Leave Management (backend leave_endpoints.py): one screen payload, approvals in the workflow engine
export const getLeaveScreen = year => get(`/leave/screen${qs({ year })}`);
export const getLeaveEvents = (params = {}) => get(`/leave/events${qs(params)}`);
export const checkLeave = body => post('/leave/requests/check', body);
export const applyLeave = body => post('/leave/requests', body);
// { ids, action: submit | pre_approve | approve | reject | return | cancel, comment } -> { done, failed }
export const leaveAction = body => post('/leave/requests/action', body);
export const previewMonetization = body => post('/leave/monetizations/preview', body);
export const createMonetization = body => post('/leave/monetizations', body);
export const monetizationAction = body => post('/leave/monetizations/action', body);
export const createAdjustment = body => post('/leave/adjustments', body);
export const previewCarryForward = body => post('/leave/carry-forward/preview', body);
export const carryForward = body => post('/leave/carry-forward', body);
export const saveGradeAllocation = body => put('/leave/grades', body);
export const saveLeaveType = (id, body) => (id ? put(`/leave/types/${id}`, body) : post('/leave/types', body));
export const saveHoliday = (id, body) => (id ? put(`/leave/holidays/${id}`, body) : post('/leave/holidays', body));
export const deleteHoliday = id => del(`/leave/holidays/${id}`);
export const listLeaveRequests = (params = {}) => all(`/leave/requests${qs(params)}`);
export const getLeaveBalances = (employeeId, year) => get(`/leave/balances/employee/${employeeId}${qs({ year })}`);
// the HR register's Add Leave form (Employee 360): submitted for approval at once
export const createLeaveRequest = b => applyLeave({
  employee_id: b.employee_id, leave_type_id: b.leave_type_id, start_date: b.start_date, end_date: b.end_date, reason: b.reason,
  reliever_id: b.reliever_employee_id || null, emergency_contact: b.emergency_contact_phone || null, handover_note: b.handover_notes || null, mode: 'submit',
});
const LEAVE_ACTION_OF = { pre_approved: 'pre_approve', approved: 'approve', rejected: 'reject', returned: 'return', cancelled: 'cancel', pending: 'submit' };
export const setLeaveRequestStatus = (id, { status, comment }) => leaveAction({ ids: [id], action: LEAVE_ACTION_OF[status] || status, comment })
  .then(r => { if (r.failed?.length) throw new Error(r.failed[0]); return r; });

export const listAccommodations = (params = {}) => all(`/accommodations${qs(params)}`);
export const createAccommodation = body => post('/accommodations', body);
export const assignAccommodation = (id, body) => post(`/accommodations/${id}/assign`, body);
export const vacateAssignment = (assignmentId, vacateDate) => put(`/accommodations/assignments/${assignmentId}/vacate${qs({ vacate_date: vacateDate })}`, {});

// ── time & attendance (backend app/api/v1/endpoints/attendance_endpoints.py) ──
export const getAttendanceDay = d => get(`/attendance/day${qs({ d })}`);
export const markAttendance = body => post('/attendance/records', body);
export const updateAttendance = (id, body) => put(`/attendance/records/${id}`, body);
export const markAllPresent = body => post('/attendance/records/mark-all-present', body);
export const listAttendanceRecords = params => get(`/attendance/records${qs(params)}`).then(items);
export const listAttendanceBatches = days => get(`/attendance/batches${qs({ days })}`);
export const attendanceBatchAction = body => post('/attendance/batches/action', body);
export const getAttendanceAnalysis = month => get(`/attendance/analysis${qs({ month })}`);
export const getAttendancePolicy = () => get('/attendance/policy');
export const saveAttendancePolicy = body => put('/attendance/policy', body);
// Mark Daily Attendance
export const getMarkOptions = () => fresh('/attendance/mark/options');
export const getMarkSheet = params => fresh(`/attendance/mark/sheet${qs(params)}`);
export const previewMarkSheet = body => post('/attendance/mark/preview', body);
export const submitMarkSheet = body => post('/attendance/mark/submit', body);
// payroll attendance: Attendance Import / Manual, Site Sheets, Check, Grid (per payroll month)
export const getPayrollCalendar = period => fresh(`/payroll/calendar${qs({ period })}`);
export const gradeSetup = () => get('/payroll/grade-setup');   // Payroll › Grade Policy Setup
export const getAttendanceImport = period => fresh(`/attendance/import${qs({ period })}`);
export const saveAttendanceImport = (period, body) => put(`/attendance/import${qs({ period })}`, body);
export const applyAttendanceImport = period => post(`/attendance/import/apply${qs({ period })}`, {});
export const clearAttendanceImport = (period, removeApplied) => del(`/attendance/import${qs({ period, remove_applied: removeApplied })}`);
export const saveAttendanceImportSettings = body => put('/attendance/import/settings', body);
export const getAttendanceManual = period => fresh(`/attendance/manual${qs({ period })}`);
export const addAttendanceManual = (period, body) => post(`/attendance/manual${qs({ period })}`, body);
export const updateAttendanceManual = (period, id, body) => put(`/attendance/manual/${id}${qs({ period })}`, body);
export const deleteAttendanceManual = id => del(`/attendance/manual/${id}`);
export const getSiteSheets = period => fresh(`/attendance/site-sheets${qs({ period })}`);
export const siteSheetAction = (period, body) => post(`/attendance/site-sheets/action${qs({ period })}`, body);
export const getAttendanceCheck = period => fresh(`/attendance/check${qs({ period })}`);
export const getAttendanceGrid = period => fresh(`/attendance/grid${qs({ period })}`);
export const getHRSettings = () => fresh('/attendance/hr-settings');
export const saveHRSettings = body => put('/attendance/hr-settings', body);
// HR Request Center (backend app/api/v1/endpoints/hr_request_endpoints.py)
export const hrrcMeta = () => fresh('/hr-requests/meta');
export const hrrcDashboard = () => fresh('/hr-requests/dashboard');
export const hrrcList = params => fresh(`/hr-requests${qs(params)}`);
export const hrrcDrafts = () => fresh('/hr-requests/drafts');
export const hrrcReports = params => fresh(`/hr-requests/reports${qs(params)}`);
export const hrrcAudit = q => fresh(`/hr-requests/audit${qs({ q })}`);
export const hrrcGet = id => fresh(`/hr-requests/${id}`);
export const hrrcCreate = body => post('/hr-requests', body);
export const hrrcUpdate = (id, body) => put(`/hr-requests/${id}`, body);
export const hrrcDelete = id => del(`/hr-requests/${id}`);
export const hrrcAction = (id, body) => post(`/hr-requests/${id}/action`, body);
export const hrrcAddFile = (id, field, file) => upload('POST', `/hr-requests/${id}/files/${field}`, form(file));
export const hrrcFileBlob = (id, fileId) => blob(`/hr-requests/${id}/files/${fileId}/download`);
export const hrrcRemoveFile = (id, fileId) => del(`/hr-requests/${id}/files/${fileId}`);
export const hrrcSettings = () => fresh('/hr-requests/settings/all');
export const hrrcSaveNumberFormat = body => put('/hr-requests/settings/number-format', body);
export const hrrcSaveType = (typeId, body) => put(`/hr-requests/settings/types/${typeId}`, body);

// HR Request Center > Advanced (Advanced Requisition and Request Management)
export const advMeta = () => fresh('/adv-requests/meta');
export const advDashboard = () => fresh('/adv-requests/dashboard');
export const advList = params => fresh(`/adv-requests${qs(params)}`);
export const advStatement = params => fresh(`/adv-requests/statement${qs(params)}`);
export const advAudit = q => fresh(`/adv-requests/audit${qs({ q })}`);
export const advGet = id => fresh(`/adv-requests/${id}`);
export const advCreate = body => post('/adv-requests', body);
export const advUpdate = (id, body) => put(`/adv-requests/${id}`, body);
export const advDelete = id => del(`/adv-requests/${id}`);
export const advAction = (id, body) => post(`/adv-requests/${id}/action`, body);
export const advLookup = (kind, q) => fresh(`/adv-requests/lookups/${kind}${qs({ q })}`);
export const advLastPurchase = params => fresh(`/adv-requests/last-purchase${qs(params)}`);
export const advBudgetCheck = params => fresh(`/adv-requests/budget-check${qs(params)}`);
export const advSectionDHistory = params => fresh(`/adv-requests/section-d-history${qs(params)}`);
export const advAddFile = (id, kind, file) => upload('POST', `/adv-requests/${id}/files${qs({ kind })}`, form(file));
export const advFileBlob = (id, fileId) => blob(`/adv-requests/${id}/files/${fileId}/download`);
export const advRemoveFile = (id, fileId) => del(`/adv-requests/${id}/files/${fileId}`);
export const advSettings = () => fresh('/adv-requests/settings/all');
export const advSaveRules = body => put('/adv-requests/settings/rules', body);
export const advSaveNumbering = body => put('/adv-requests/settings/numbering', body);
export const advSaveType = (id, body) => (id ? put(`/adv-requests/types/${id}`, body) : post('/adv-requests/types', body));
export const advToggleType = id => post(`/adv-requests/types/${id}/toggle`, {});
export const advBudgets = () => fresh('/adv-requests/budgets');
export const advSaveBudget = (id, body) => (id ? put(`/adv-requests/budgets/${id}`, body) : post('/adv-requests/budgets', body));
export const advBudgetAction = (id, body) => post(`/adv-requests/budgets/${id}/action`, body);

// HR Expenses
export const hreMeta = () => fresh('/hr-expenses/meta');
export const hreList = params => fresh(`/hr-expenses${qs(params)}`);
export const hreGet = id => fresh(`/hr-expenses/${id}`);
export const hreCreate = body => post('/hr-expenses', body);
export const hreUpdate = (id, body) => put(`/hr-expenses/${id}`, body);
export const hreDelete = id => del(`/hr-expenses/${id}`);
export const hreAction = (id, body) => post(`/hr-expenses/${id}/action`, body);
export const hreMessage = body => post('/hr-expenses/message', body);
export const hreBudgets = () => fresh('/hr-expenses/budgets');
export const hreSetBudget = (departmentId, body) => put(`/hr-expenses/budgets/${departmentId}`, body);
export const hreTypes = () => fresh('/hr-expenses/types');
export const hreSaveType = (id, body) => (id ? put(`/hr-expenses/types/${id}`, body) : post('/hr-expenses/types', body));
export const hreDeleteType = id => del(`/hr-expenses/types/${id}`);
// attendance edit log (corrections)
export const getEditLog = params => get(`/attendance/edit-log${qs(params)}`);
export const getCorrection = id => get(`/attendance/edit-log/${id}`);
export const updateCorrection = (id, body) => put(`/attendance/edit-log/${id}`, body);
export const correctionAction = (id, body) => post(`/attendance/edit-log/${id}/action`, body);
// overtime authorization
export const getOvertimeRegister = () => get('/attendance/overtime');
export const getOvertimeEstimate = params => get(`/attendance/overtime/estimate${qs(params)}`);
export const getOvertime = id => get(`/attendance/overtime/${id}`);
export const createOvertime = body => post('/attendance/overtime', body);
export const overtimeAction = body => post('/attendance/overtime/action', body);
// biometric registrations
export const getBiometrics = params => get(`/attendance/biometrics${qs(params)}`);
export const getBiometricEmployees = () => get('/attendance/biometrics/employees');
export const getBiometric = id => get(`/attendance/biometrics/${id}`);
export const createBiometric = body => post('/attendance/biometrics', body);
export const updateBiometric = (id, body) => put(`/attendance/biometrics/${id}`, body);
export const deleteBiometric = id => del(`/attendance/biometrics/${id}`);
// Missed Days Management (phase 4; backend missed_days_endpoints.py)
export const mdDashboard = month => get(`/missed-days/dashboard${qs({ month })}`);
export const mdGenerate = body => post('/missed-days/generate', body);
export const mdRefresh = month => post('/missed-days/refresh', { month });
export const mdReview = (params = {}) => get(`/missed-days/review${qs(params)}`);
export const mdReviewAction = body => post('/missed-days/review/action', body);
export const mdSupervisorAction = body => post('/missed-days/supervisor/action', body);
export const mdAssignWithDocument = (ids, leaveType, note, file) => {
  const fd = new FormData();
  fd.append('ids', ids.join(','));
  fd.append('leave_type', leaveType);
  if (note) fd.append('note', note);
  fd.append('file', file);
  return upload('POST', '/missed-days/review/assign-with-document', fd);
};
export const mdRegister = (params = {}) => get(`/missed-days/register${qs(params)}`);
export const mdRegisterCreate = body => post('/missed-days/register', body);
export const mdRegisterUpdate = (id, body) => put(`/missed-days/register/${id}`, body);
export const mdRegisterDelete = id => del(`/missed-days/register/${id}`);
export const whEmployees = () => get('/work-history');
export const whEmployee = id => get(`/work-history/${id}`);
export const whAddEvent = body => post('/work-history/events', body);
export const whResubmit = id => post(`/work-history/events/${id}/resubmit`, {});
export const whDeleteEvent = id => del(`/work-history/events/${id}`);
export const mdStatement = (params = {}) => get(`/missed-days/statement${qs(params)}`);
export const mdStatementEmployee = (id, params = {}) => get(`/missed-days/statement/${id}${qs(params)}`);
export const mdHistory = (params = {}) => get(`/missed-days/history${qs(params)}`);
export const mdHistoryEmployee = id => get(`/missed-days/history/${id}`);
export const mdHistoryAction = (id, body) => post(`/missed-days/history/${id}/action`, body);
export const mdLeave = () => get('/missed-days/leave');
export const mdLeaveAuto = dryRun => post('/missed-days/leave/auto', { dry_run: dryRun });
export const mdLeaveAssign = body => post('/missed-days/leave/assign', body);
export const mdPayroll = month => get(`/missed-days/payroll${qs({ month })}`);
export const mdPayrollPost = ids => post('/missed-days/payroll/post', { ids });
export const mdCost = (params = {}) => get(`/missed-days/cost${qs(params)}`);
export const mdCostImpact = body => put('/missed-days/cost/impact', body);
export const mdCostAudit = (source, sourceId) => get(`/missed-days/cost/audit${qs({ source, source_id: sourceId })}`);
export const mdEvidence = costId => get(`/missed-days/cost/${costId}/evidence`);
export const mdAddEvidence = (costId, file) => upload('POST', `/missed-days/cost/${costId}/evidence`, form(file));
export const mdEvidenceBlob = fileId => blob(`/missed-days/evidence/${fileId}`);
export const mdDeleteEvidence = fileId => del(`/missed-days/evidence/${fileId}`);
export const mdLateness = (params = {}) => get(`/missed-days/lateness${qs(params)}`);
export const mdLatenessEmployee = (id, params = {}) => get(`/missed-days/lateness/${id}${qs(params)}`);
export const mdLatenessAction = body => post('/missed-days/lateness/action', body);
export const mdSettings = () => get('/missed-days/settings');
export const mdSaveSettings = body => put('/missed-days/settings', body);
export const mdAudit = (params = {}) => get(`/missed-days/audit${qs(params)}`);

// Timesheet Register and Attendance Report (phase 3)
export const getTimesheets = period => get(`/attendance/timesheets${qs({ period })}`);
export const generateTimesheets = period => post('/attendance/timesheets/generate', { period });
export const getTimesheet = id => get(`/attendance/timesheets/${id}`);
export const updateTimesheet = (id, body) => put(`/attendance/timesheets/${id}`, body);
// { ids, action: submit | approve | reject | return | lock, comment } -> { message, done, skipped }
export const timesheetAction = body => post('/attendance/timesheets/action', body);
export const getAttendanceReport = (params = {}) => get(`/attendance/report${qs(params)}`);
export const getAttendanceReportDay = (employeeId, d) => get(`/attendance/report/day${qs({ employee_id: employeeId, d })}`);
export const getStatutoryRates = () => get('/attendance/statutory');
export const saveStatutoryRates = body => put('/attendance/statutory', body);

// ── talent & movement ────────────────────────────────────────────────────────
export const listApplicants = (params = {}) => all(`/talent/applicants${qs({ limit: PAGE, ...params })}`);
export const createApplicant = body => post('/talent/applicants', body);
export const updateApplicant = (id, body) => put(`/talent/applicants/${id}`, body);
export const hireApplicant = (id, body) => post(`/talent/applicants/${id}/hire`, body);
export const deleteApplicant = id => del(`/talent/applicants/${id}`);
export const listCases = (params = {}) => all(`/talent/cases${qs({ limit: PAGE, ...params })}`);
export const createCase = body => post('/talent/cases', body);
export const setCaseStatus = (id, body) => put(`/talent/cases/${id}/status`, body);
export const listReviews = (params = {}) => all(`/talent/reviews${qs({ limit: PAGE, ...params })}`);
export const createReview = body => post('/talent/reviews', body);
export const listSalaryHistory = employeeId => all(`/talent/salary-history/employee/${employeeId}`);
export const createSalaryChange = body => post('/talent/salary-history', body);

// ── compliance, badging & HMO ────────────────────────────────────────────────
export const getComplianceOverview = () => get('/compliance/overview');
export const listIdCards = (params = {}) => all(`/compliance/id-cards${qs({ limit: PAGE, ...params })}`);
export const getEmployeeIdCard = employeeId => get(`/compliance/id-cards/employee/${employeeId}`);
export const requestIdCard = body => post('/compliance/id-cards', body);
export const updateIdCard = (id, body) => put(`/compliance/id-cards/${id}`, body);
export const listDocuments = (params = {}) => all(`/compliance/documents${qs(params)}`);
// Employee Document Checklist (Stephanet's doc checklist; backend/app/services/doc_checklist_service.py)
// one page of the matrix: { groups, requirements, items, total, summary }
export const documentChecklistPage = (params = {}) => get(`/compliance/document-checklist${qs(params)}`);
export const employeeDocChecklist = employeeId => get(`/compliance/document-checklist/employees/${employeeId}`);
export const updateDocChecklistCell = (employeeId, requirementId, body) => put(`/compliance/document-checklist/${employeeId}/${requirementId}`, body);
export const listDocRequirements = () => get('/compliance/doc-requirements');
export const createDocRequirement = body => post('/compliance/doc-requirements', body);
export const updateDocRequirement = (id, body) => put(`/compliance/doc-requirements/${id}`, body);
export const resetDocRequirements = () => post('/compliance/doc-requirements/reset');
export const listEmployeeDocuments = employeeId => all(`/compliance/documents/employee/${employeeId}`);
export const createDocument = body => post('/compliance/documents', body);
export const verifyDocument = id => put(`/compliance/documents/${id}/verify`, {});
export const deleteDocument = id => del(`/compliance/documents/${id}`);
export const listHmo = (params = {}) => all(`/compliance/hmo${qs({ limit: PAGE, ...params })}`);
export const enrollHmo = body => post('/compliance/hmo', body);
export const listSitePermits = (params = {}) => all(`/compliance/site-permits${qs(params)}`);
export const issueSitePermit = body => post('/compliance/site-permits', body);

// ── uploaded files (backend/app/api/v1/endpoints/hr_file_endpoints.py) ─────
// one route per kind of file; each accepts only its own types (src/hr/fileRules.js)
const form = (file, extra = {}) => {
  const fd = new FormData();
  fd.append('file', file);
  Object.entries(extra).forEach(([k, v]) => { if (v !== undefined && v !== null && v !== '') fd.append(k, v); });
  return fd;
};
// Either may be the signed-in person's own photo, so the top-bar/sidebar avatars are told to refetch.
const photoChanged = (r) => { window.dispatchEvent(new Event('my-photo-changed')); return r; };
export const uploadPhoto = (employeeId, file) => upload('PUT', `/employees/${employeeId}/photo`, form(file)).then(photoChanged);
export const deletePhoto = employeeId => del(`/employees/${employeeId}/photo`).then(photoChanged);
export const photoBlob = employeeId => blob(`/employees/${employeeId}/photo`);
export const uploadCv = (employeeId, file) => upload('PUT', `/employees/${employeeId}/cv`, form(file));
export const uploadIdDocument = (employeeId, file) => upload('PUT', `/employees/${employeeId}/id-document`, form(file));
export const uploadCertificate = (employeeId, file, title) => upload('POST', `/employees/${employeeId}/certificates`, form(file, { title }));
export const listEmployeeFiles = employeeId => get(`/employees/${employeeId}/files`);
// the full profile report, built on the server with ReportLab (GET /employees/{id}/report.pdf)
export const employeeReportBlob = employeeId => blob(`/employees/${employeeId}/report.pdf`);
export const uploadDocumentFile = (docId, file) => upload('PUT', `/compliance/documents/${docId}/file`, form(file));
export const deleteDocumentFile = docId => del(`/compliance/documents/${docId}/file`);
export const documentFileBlob = docId => blob(`/compliance/documents/${docId}/file`);
export const uploadLeaveAttachment = (requestId, file) => upload('PUT', `/leave/requests/${requestId}/attachment`, form(file));
export const deleteLeaveAttachment = requestId => del(`/leave/requests/${requestId}/attachment`);
export const leaveAttachmentBlob = requestId => blob(`/leave/requests/${requestId}/attachment`);
export const uploadChecklistFile = (employeeId, requirementId, file) => upload('POST', `/compliance/document-checklist/${employeeId}/${requirementId}/files`, form(file));
export const checklistFileBlob = fileId => blob(`/compliance/document-checklist/files/${fileId}`);
export const deleteChecklistFile = fileId => del(`/compliance/document-checklist/files/${fileId}`);

// biometric devices (ZKTeco ADMS receiver: backend/app/adms; HR side in biometric_device_endpoints.py)
export const getDevices = () => fresh('/biometric-devices');
export const getDevice = id => fresh(`/biometric-devices/${id}`);
export const createDevice = body => post('/biometric-devices', body);
export const updateDevice = (id, body) => put(`/biometric-devices/${id}`, body);
export const deleteDevice = id => del(`/biometric-devices/${id}`);
export const queueDeviceCommand = (id, body) => post(`/biometric-devices/${id}/commands`, body);
export const cancelDeviceCommand = id => post(`/biometric-devices/commands/${id}/cancel`, {});
export const getPunches = params => fresh(`/biometric-devices/punches${qs(params)}`);
export const getUnmatchedPunches = () => fresh('/biometric-devices/unmatched');
export const linkPunchPin = body => post('/biometric-devices/unmatched/link', body);
export const getDeviceLog = params => fresh(`/biometric-devices/raw-log${qs(params)}`);
export const pushDeviceUsers = id => post(`/biometric-devices/${id}/push-users`, {});
export const getPunchExceptions = () => fresh('/biometric-devices/exceptions');
export const reviewPunches = body => post('/biometric-devices/punches/review', body);
export const processPunches = () => post('/biometric-devices/process', {});
export const reprocessPunches = body => post('/biometric-devices/reprocess', body);
export const pushBiometric = id => post(`/attendance/biometrics/${id}/push`, {});
export const clearBiometricTemplates = id => del(`/attendance/biometrics/${id}/templates`);
